// Library imports
import React, { useState } from "react";

// User Component imports
import { VIDEO_FORMAT_LABEL, AUDIO_WARN_INLINE } from "../utils/videoFile";
import OptimizeStatus, { AutoMiniBadge, AutoMiniToggle } from "./optimize/OptimizeStatus";

// 「idle 動画（待機状態）」を種別名と括弧内の説明に分ける（括弧の無いラベルはそのまま）
const splitSlotLabel = (label) => {
    const m = /^(.*?)（(.*)）$/.exec(label || "");
    return m ? { main: m[1].trim(), sub: m[2] } : { main: label, sub: "" };
};

// 添付した動画のサムネイルとファイル名
const AttachedFile = ({ entry, caption }) => (
    <div className="ops-video-slot-new">
        {entry.previewUrl ?
            <video className="ops-video-slot-thumb" src={entry.previewUrl} muted loop playsInline autoPlay />
            :
            <div className="ops-video-slot-thumb is-empty"><i className="bi bi-film" /></div>
        }
        <div className="ops-video-slot-col">
            <span className="ops-video-slot-cap is-new">{caption}{entry.auto && <AutoMiniBadge small />}</span>
            <span className="ops-video-slot-path is-new" title={entry.name}>{entry.name}</span>
        </div>
    </div>
);

/**
 * 動画1種別の行（追加・編集で共通）。行そのものがドロップ先になる
 * mode="add" は未添付／添付するファイル、mode="edit" は「現在 → 差し替え後」を並べる
 */
const VideoSlotRow = ({ slot, mode, current, overwrite, entry, disabled, onPick, onDrop, onClear, onToggleOptimize, miniToggle, miniLabel }) => {
    const [isOver, setIsOver] = useState(false);
    const isEdit = mode === "edit";
    const label = splitSlotLabel(slot.label);

    let body;
    if (entry) {
        body = isEdit ?
            <div className="ops-video-slot-change">
                <div className="ops-video-slot-col">
                    <span className="ops-video-slot-cap">現在</span>
                    <span className={"ops-video-slot-path is-old" + (current && !overwrite ? " is-replaced" : "")} title={current || "未設定"}>{current || "未設定"}</span>
                </div>
                <i className="bi bi-arrow-right ops-video-slot-arrow" />
                <AttachedFile entry={entry} caption={!current ? "新規追加" : overwrite ? "差し替え後（同名で上書き）" : "差し替え後"} />
            </div>
            :
            <AttachedFile entry={entry} caption="添付するファイル" />;
    } else {
        body = isEdit ?
            <div className="ops-video-slot-col">
                <span className="ops-video-slot-cap">現在のファイル</span>
                <span className={"ops-video-slot-path" + (current ? "" : " is-empty")} title={current || "未設定"}>{current || "未設定"}</span>
            </div>
            :
            <div className="ops-video-slot-col">
                <span className="ops-video-slot-cap">未添付</span>
                <span className="ops-video-slot-path is-empty">ドラッグ＆ドロップ、またはファイルを選択（{VIDEO_FORMAT_LABEL}）</span>
            </div>;
    }

    const pickLabel = entry ? "選び直す" : (!isEdit ? "ファイルを選択" : (current ? "差し替える" : "追加する"));
    const clearTitle = isEdit ? "この差し替えを取り消して、現在のファイルのままにします" : "添付を外します";

    return (
        <div className={"ops-video-slot" + (entry ? " has-file" : "") + (isOver ? " is-over" : "")}
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); if (!disabled) setIsOver(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsOver(false); }}
            onDrop={(e) => { e.preventDefault(); setIsOver(false); if (!disabled) onDrop(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
            <span className="ops-video-slot-label">
                <span className="ops-video-slot-label-main">
                    {label.main}
                    {!isEdit && slot.required && <span className="ops-required-mark">※必須</span>}
                </span>
                {label.sub && <span className="ops-video-slot-label-sub">{label.sub}</span>}
            </span>
            {body}
            <div className="ops-video-slot-actions">
                <button type="button" className="ops-pill ops-video-slot-pick" onClick={onPick} disabled={disabled}>
                    <i className="bi bi-upload" />{pickLabel}
                </button>
                {entry &&
                    <button type="button" className="ops-video-slot-clear" onClick={onClear} disabled={disabled} title={clearTitle}>
                        <i className="bi bi-x-lg" />
                    </button>
                }
            </div>
            <OptimizeStatus entry={entry} disabled={disabled} onToggle={onToggleOptimize} className="ops-video-slot-note" />
            {entry && entry.hasAudio === true && <div className="ops-video-slot-note ops-warn-text"><i className="bi bi-exclamation-triangle-fill" />{AUDIO_WARN_INLINE}</div>}
            {entry && entry.bad && <div className="ops-video-slot-note ops-error-text"><i className="bi bi-x-circle-fill" />{VIDEO_FORMAT_LABEL} ファイルを選択してください（ファイル名に絵文字などは使えません）</div>}
            {miniToggle && <AutoMiniToggle toggle={miniToggle} disabled={disabled || miniToggle.disabled} label={miniLabel} className="ops-video-slot-mini" />}
            {isOver && <div className="ops-video-slot-hint"><i className="bi bi-box-arrow-in-down" />ここにドロップして「{slot.label}」に設定</div>}
        </div>
    );
};

export default VideoSlotRow;
